import { useEffect, useMemo, useRef, useState } from "react";
import { ShoppingBag, MapPin, X } from "lucide-react";
import { api } from "../lib/api";
import { NAMES, CITIES } from "../lib/socialProofData";

/**
 * Floating buyer-activity notification card (bottom-left).
 * Pops in a synthetic but realistic purchase event every 9–16 seconds.
 *
 * - Each notification: name, city, "just bought" + REAL product name (pulled
 *   from /api/products). Mix of "X minutes ago" timing for authenticity.
 * - Slide-in from the left, auto-dismiss after ~5.5s, idle gap, repeat.
 * - Once a user clicks the X, the widget hides for the rest of the session.
 *
 * Curated regional pool covers USA states/cities, Europe, UK, Australia, NZ,
 * India, China, Hong Kong, Japan, Malaysia, Indonesia, Vietnam, Canada,
 * Philippines, plus Africa, Middle East, LATAM, Singapore.
 *
 * Data is representative/synthetic for social proof — combined with real
 * AIGL product names for relevance.
 */
const SESSION_DISMISS_KEY = "aigl-live-activity-dismissed";

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function makeEvent(productNames) {
  const minutesAgo = 1 + Math.floor(Math.random() * 9); // 1–9 mins ago
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: pick(NAMES),
    place: pick(CITIES),
    product: pick(productNames) || "AIGL Business Plan",
    minutesAgo,
  };
}

export const LiveActivityFeed = () => {
  const [productNames, setProductNames] = useState([]);
  const [current, setCurrent] = useState(null);
  const [phase, setPhase] = useState("hidden"); // hidden | in | out
  const [dismissed, setDismissed] = useState(false);
  const timersRef = useRef([]);

  // Read session-dismissed flag once
  useEffect(() => {
    try {
      if (sessionStorage.getItem(SESSION_DISMISS_KEY) === "1") setDismissed(true);
    } catch {
      // noop
    }
  }, []);

  // Load real product names so notifications mention actual plans
  useEffect(() => {
    api
      .get("/products?currency=USD")
      .then((r) => {
        const names = (r.data?.products || [])
          .map((p) => p.name)
          .filter(Boolean);
        setProductNames(names);
      })
      .catch(() => setProductNames([]));
  }, []);

  // Lifecycle: wait 4s on first paint → pop event → show 5.5s → hide → 8–12s idle → repeat
  useEffect(() => {
    if (dismissed) return;
    if (productNames.length === 0) return;

    const clear = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    const cycle = (initialDelay = 4000) => {
      clear();
      const t1 = setTimeout(() => {
        setCurrent(makeEvent(productNames));
        setPhase("in");
        const t2 = setTimeout(() => {
          setPhase("out");
          const t3 = setTimeout(() => {
            setPhase("hidden");
            // idle gap 7–14 seconds
            cycle(7000 + Math.floor(Math.random() * 7000));
          }, 600); // exit transition
          timersRef.current.push(t3);
        }, 5500);
        timersRef.current.push(t2);
      }, initialDelay);
      timersRef.current.push(t1);
    };

    cycle();
    return clear;
  }, [productNames, dismissed]);

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SESSION_DISMISS_KEY, "1");
    } catch {
      // noop
    }
    setDismissed(true);
  };

  if (dismissed || !current || phase === "hidden") return null;

  return (
    <div
      data-testid="live-activity-feed"
      className={`fixed left-3 sm:left-5 bottom-3 sm:bottom-5 z-[80] max-w-[330px] sm:max-w-[360px] transition-all duration-500 ${
        phase === "in"
          ? "translate-x-0 opacity-100"
          : "-translate-x-6 opacity-0"
      }`}
      style={{ paddingBottom: "max(env(safe-area-inset-bottom), 0px)" }}
      role="status"
      aria-live="polite"
    >
      <div className="bg-white border border-slate-200 shadow-2xl shadow-[#0A192F]/20 overflow-hidden">
        <div className="flex items-start gap-3 p-3.5">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#0A192F] text-[#C5A059] flex items-center justify-center">
            <ShoppingBag className="w-4 h-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[13px] text-slate-700 leading-snug">
              <b className="text-[#0A192F]">{current.name}</b>
              {" "}from{" "}
              <span className="text-slate-600">
                <MapPin className="inline w-3 h-3 mr-0.5 -mt-0.5 text-[#C5A059]" />
                {current.place.city},{" "}
                <span className="text-slate-400">{current.place.region}</span>
              </span>
            </p>
            <p className="text-[12px] text-slate-500 mt-1 line-clamp-2">
              just purchased{" "}
              <span className="text-[#0A192F] font-medium">&ldquo;{current.product}&rdquo;</span>
            </p>
            <div className="flex items-center justify-between mt-1.5">
              <div className="flex items-center gap-1 text-[10px] font-accent tracking-[1.5px] text-emerald-600">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                VERIFIED · {current.minutesAgo} min ago
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss live activity"
            data-testid="live-activity-dismiss"
            className="-mt-0.5 -mr-0.5 w-7 h-7 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded transition"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="h-0.5 bg-[#C5A059]/30">
          <div
            className="h-full bg-[#C5A059] origin-left"
            style={{
              animation: phase === "in" ? "live-activity-progress 5.5s linear forwards" : "none",
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes live-activity-progress {
          from { transform: scaleX(1); }
          to { transform: scaleX(0); }
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Live Shoppers Counter Strip — thin gold-tinted bar with a fluctuating count
// ---------------------------------------------------------------------------

function pickInitialCount() {
  // Time-of-day driven baseline so concurrent visitors see roughly aligned numbers.
  // Range ~22 (graveyard) to ~58 (peak afternoon UTC).
  const hour = new Date().getUTCHours();
  // Peak around 14–18 UTC (covers EU+US overlap), trough 4–8 UTC
  const center = 40 + 18 * Math.sin(((hour - 6) / 24) * Math.PI * 2);
  // Add a small offset by minute so it doesn't look frozen across reloads
  const offset = Math.floor((Date.now() / 60000) % 7) - 3;
  return Math.max(22, Math.min(58, Math.round(center + offset)));
}

export const LiveShoppersBar = () => {
  const initial = useMemo(pickInitialCount, []);
  const [count, setCount] = useState(initial);

  useEffect(() => {
    // Random walk: every 4–9s, ±1 with occasional ±2 swings
    const tick = () => {
      setCount((c) => {
        const drift = Math.random() < 0.18 ? 2 : 1;
        const dir = Math.random() < 0.5 ? -1 : 1;
        const next = c + dir * drift;
        // Keep within plausible range; if too far from current hour baseline,
        // bias it gently back home.
        const baseline = pickInitialCount();
        if (next < 18) return c + 1;
        if (next > 62) return c - 1;
        if (Math.abs(next - baseline) > 20) {
          return c + (baseline > c ? 1 : -1);
        }
        return next;
      });
      schedule();
    };
    let timer;
    const schedule = () => {
      timer = setTimeout(tick, 4000 + Math.random() * 5000);
    };
    schedule();
    return () => clearTimeout(timer);
  }, []);

  return (
    <div
      data-testid="live-shoppers-bar"
      className="bg-[#0A192F] border-t border-[#C5A059]/15"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-2.5 flex items-center justify-center gap-2.5 text-[13px] sm:text-sm">
        <span className="relative inline-flex w-2.5 h-2.5">
          <span className="absolute inset-0 rounded-full bg-emerald-400 opacity-75 animate-ping" />
          <span className="relative w-2.5 h-2.5 rounded-full bg-emerald-500" />
        </span>
        <span className="text-white/90">
          <b data-testid="live-shoppers-count" className="text-[#C5A059] tabular-nums">
            {count}
          </b>{" "}
          founders shopping right now
        </span>
        <span className="hidden sm:inline text-white/30 mx-1">·</span>
        <span className="hidden sm:inline text-white/60 text-[12px]">
          Plans are flying — secure yours below
        </span>
      </div>
    </div>
  );
};
